import { Spacing } from '@persona-id/design-tokens';
import Fuse from 'fuse.js';
import { Fragment, useMemo, useState } from 'react';
import styled from 'styled-components';

import Tooltip from 'dashboard/components/Tooltip';
import SearchInput from 'dashboard/components-lib/SearchInput';
import { ModalBody, ModalHeader } from 'dashboard/layouts/ModalLayout';
import {
  Blueprint,
  BlueprintIconKey,
  blueprintIconsMap,
  BlueprintServerMetadata,
} from 'dashboard/models/blueprint';
import { Organization, OrganizationStatus } from 'dashboard/models/organization';
import { OrganizationPlan } from 'dashboard/models/organization-plan';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Label, Paragraph } from 'design-system/components/Text';

export interface BlueprintsAllModalContentsProps {
  previouslyUsedBlueprints: Blueprint[];
  blueprintsCatalogServer: BlueprintServerMetadata[];
  organization: Organization;
  currentOrganizationPlan: OrganizationPlan;
  toBlueprintPreview: (blueprintKey: string) => void;
  createBlueprint: (blueprintKey: string) => void;
  closeModal: () => void;
}

// These are solutions that we want to market in our catalog, but we don't have supported Blueprints for them on the server,
// hence we just hardcode them here with `is-usable-in-dashboard: false`.
const additionalBlueprintOfferings: BlueprintServerMetadata[] = [
  {
    key: 'kyb',
    'display-name': 'Know Your Business (KYB)',
    description:
      'Streamline and automate your Know Your Business (KYB) process by verifying business details against global business registries and available public information. Can also include the ability to check against watchlists, adverse media hits, non-profit information, and verification of beneficial owners (UBOs) in a seamless experience.',
    'display-icon-keys': [
      BlueprintIconKey.GovIdImage,
      BlueprintIconKey.SelfieImage,
      BlueprintIconKey.WatchlistImage,
      BlueprintIconKey.PepImage,
    ],
    'is-usable-in-dashboard': false,
    'is-viewable-in-dashboard': true,
    'required-self-serve-plan-keys-in-production-environment': [],
  },
  {
    key: 'business_verification_us',
    'display-name': 'Business Verification (US)',
    description:
      'Collect business details to conduct lightweight checks against business registries, watchlists, and adverse media hits. This solution does not include UBO verification or associated persons verification.',
    'display-icon-keys': [
      BlueprintIconKey.GovIdImage,
      BlueprintIconKey.SelfieImage,
      BlueprintIconKey.WatchlistImage,
      BlueprintIconKey.PepImage,
    ],
    'is-usable-in-dashboard': false,
    'is-viewable-in-dashboard': true,
    'required-self-serve-plan-keys-in-production-environment': [],
  },
];

const BlueprintsAllModalContents = ({
  previouslyUsedBlueprints,
  blueprintsCatalogServer,
  organization,
  currentOrganizationPlan,
  toBlueprintPreview,
  createBlueprint,
  closeModal,
}: BlueprintsAllModalContentsProps) => {
  const [searchTerm, setSearchTerm] = useState<string>('');

  const finalBlueprintsCatalogServer = blueprintsCatalogServer
    ? [...blueprintsCatalogServer, ...additionalBlueprintOfferings].sort((a, b) =>
        a['display-name'].localeCompare(b['display-name']),
      )
    : [];

  const fuse = useMemo(() => {
    return new Fuse(finalBlueprintsCatalogServer, {
      shouldSort: false,
      threshold: 0.3,
      ignoreLocation: true,
      keys: [
        { name: 'display-name', weight: 0.7 },
        { name: 'description', weight: 0.3 },
      ],
    });
  }, [finalBlueprintsCatalogServer]);

  const searchResults: Array<BlueprintServerMetadata> | null = useMemo(() => {
    if (searchTerm.length === 0) {
      return finalBlueprintsCatalogServer;
    } else {
      return fuse.search(searchTerm).map(({ item }) => item);
    }
  }, [finalBlueprintsCatalogServer, fuse, searchTerm]);

  const canCreateBlueprint = (
    blueprintKey: string,
    organization: Organization,
    currentOrganizationPlan: OrganizationPlan,
  ) => {
    if (organization.attributes.status === OrganizationStatus.New) return true;
    if (organization.attributes.status === OrganizationStatus.Deactivated) return false;
    if (organization.attributes.status === OrganizationStatus.Active) {
      // This is an interim hack/solution to handle the fact that we currently only define the required plans for a blueprint
      // for "self service" plans which have locked feature access. If they do NOT have locked feature access, then we
      // currently are unable to determine if they can effectively use the BP so we just make the assumption that they can
      // or will be able to.
      if (currentOrganizationPlan?.attributes?.['product-feature-access-is-locked'] === false)
        return true;

      const blueprintServerMetadata = finalBlueprintsCatalogServer.find(
        (bp) => blueprintKey === bp['key'],
      );

      const organizationPlanKeysSupportedInProductEnvironment =
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        blueprintServerMetadata['required-self-serve-plan-keys-in-production-environment'];

      return organizationPlanKeysSupportedInProductEnvironment.includes(
        currentOrganizationPlan?.attributes?.['key'],
      );
    }

    return false;
  };

  // blueprintsCatalogServer can be empty if network request fails or user has insufficient permissions
  if (!blueprintsCatalogServer || blueprintsCatalogServer.length === 0) {
    return (
      <>
        <ModalHeader onClose={closeModal}>Explore solutions</ModalHeader>
        <ModalBody>
          <Paragraph color='primary'>
            Sorry, something went wrong. We were not able to load the pre-built solutions.
          </Paragraph>
        </ModalBody>
      </>
    );
  }

  return (
    <>
      <ModalHeader onClose={closeModal}>Explore solutions</ModalHeader>

      <ModalBody>
        <SearchInput
          id='blueprints-all-modal__search'
          title={null}
          placeholder='Search...'
          value={searchTerm}
          handleInput={(term) => setSearchTerm(term)}
          useClearIcon
        />
        <Spacer height={2} />

        <BlueprintCardsWrapper>
          {searchResults.length === 0 ? (
            <Paragraph size='md'>No results. Try changing your search and/or filter(s).</Paragraph>
          ) : (
            <>
              {searchResults.map((result) => {
                const usedBlueprint = previouslyUsedBlueprints.filter(
                  (bp) => bp['attributes']['source-type'] === result['key'],
                );
                // We want to check if the organization has used this Blueprint before and at least one of those
                // resulting Solutions is not archived, so that we can show "Solution is already added"
                let wasPreviouslyUsedAndNotArchived = false;
                if (
                  usedBlueprint.length > 0 &&
                  usedBlueprint.some((element) => element['attributes']['archived-at'] == null)
                ) {
                  wasPreviouslyUsedAndNotArchived = true;
                }
                return (
                  <BlueprintCard
                    key={`blueprint-${result['key']}`}
                    name={result['key']}
                    label={result['display-name']}
                    iconKeys={result['display-icon-keys']}
                    description={result['description']}
                    isAvailableSelfServe={result['is-usable-in-dashboard']}
                    isAllowedToUse={canCreateBlueprint(
                      result['key'],
                      organization,
                      currentOrganizationPlan,
                    )}
                    wasPreviouslyUsedAndNotArchived={wasPreviouslyUsedAndNotArchived}
                    createBlueprint={createBlueprint}
                    toBlueprintPreview={toBlueprintPreview}
                  />
                );
              })}
            </>
          )}
        </BlueprintCardsWrapper>
      </ModalBody>
    </>
  );
};

interface BlueprintCardProps {
  name: string;
  label: string;
  iconKeys: BlueprintIconKey[];
  description: string;
  isAvailableSelfServe: boolean;
  isAllowedToUse: boolean;
  wasPreviouslyUsedAndNotArchived: boolean;
  createBlueprint: (blueprintKey: string) => void;
  toBlueprintPreview: (blueprintKey: string) => void;
}

const CONTACT_SALES_PAGE =
  'https://withpersona.com/contact?utm_source=product&utm_medium=referral&utm_audience=a&utm_campaign=acq_gen_ds_general-in-dashboard-plg-pls';

const BlueprintCard = ({
  name,
  label,
  iconKeys,
  description,
  isAvailableSelfServe,
  isAllowedToUse,
  wasPreviouslyUsedAndNotArchived,
  createBlueprint,
  toBlueprintPreview,
}: BlueprintCardProps) => {
  const renderTextForAddSolutionButton = () => {
    if (wasPreviouslyUsedAndNotArchived) {
      return 'Solution is already added';
    }
    if (!isAvailableSelfServe) {
      return 'Contact us for this solution';
    }
    return 'Add solution';
  };

  return (
    <BlueprintCardWrapper>
      <BlueprintCardBody>
        <Card.Body>
          <Label size='lg' weight='bold'>
            {label}
          </Label>
          <Spacer height={1} />
          <BlueprintIconWrapper>
            {iconKeys.map((iconKey, index) => {
              const IconComponent = blueprintIconsMap[iconKey];
              if (index === iconKeys.length - 1) {
                return (
                  <div key={`blueprint-${name}-icon-${index}`}>
                    <IconComponent />
                  </div>
                );
              } else {
                return (
                  <Fragment key={`blueprint-${name}-icon-${index}`}>
                    <div>
                      <IconComponent />
                    </div>
                    <div>+</div>
                  </Fragment>
                );
              }
            })}
          </BlueprintIconWrapper>
          <Spacer height={1} />
          <Paragraph color='secondary'>{description}</Paragraph>
        </Card.Body>
        <BlueprintCardFooter>
          {isAvailableSelfServe ? (
            <>
              {!wasPreviouslyUsedAndNotArchived && isAllowedToUse && (
                <Button
                  id='blueprints-all-modal__preview'
                  data-test='blueprints-all-modal__preview'
                  width='full'
                  variant='basic'
                  type='button'
                  onClick={() => toBlueprintPreview(name)}
                >
                  Preview this solution
                </Button>
              )}
              {isAllowedToUse ? (
                <Button
                  disabled={wasPreviouslyUsedAndNotArchived}
                  width='full'
                  variant='primary'
                  type='button'
                  onClick={() => createBlueprint(name)}
                >
                  {renderTextForAddSolutionButton()}
                </Button>
              ) : (
                <Tooltip
                  tooltip='This solution is not available on your current Plan'
                  hideArrow={false}
                  placement='bottom'
                >
                  <Button disabled={true} width='full' variant='primary' type='button'>
                    Add solution
                  </Button>
                </Tooltip>
              )}
            </>
          ) : (
            <Tooltip
              tooltip='This solution is available for Growth and Enterprise customers. Schedule a call with us to get started!'
              hideArrow={false}
              disabled={wasPreviouslyUsedAndNotArchived}
              placement='bottom'
            >
              <Button
                disabled={wasPreviouslyUsedAndNotArchived}
                width='full'
                variant='primary'
                type='button'
                onClick={() => {
                  window.open(`${CONTACT_SALES_PAGE}`, '_blank');
                }}
              >
                {renderTextForAddSolutionButton()}
              </Button>
            </Tooltip>
          )}
        </BlueprintCardFooter>
      </BlueprintCardBody>
    </BlueprintCardWrapper>
  );
};

const BlueprintCardsWrapper = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${Spacing[4]};
`;

const BlueprintCardWrapper = styled(Card)`
  border: 1px solid ${({ theme }) => theme.color.border.primary};
  border-radius: ${({ theme }) => theme.card.borderRadius};
  overflow: hidden;
`;

const BlueprintCardBody = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`;

const BlueprintCardFooter = styled('div')`
  display: flex;
  flex-direction: row;
  column-gap: ${Spacing[2]};
  padding: 0px ${Spacing[5]} ${Spacing[5]} ${Spacing[5]};
`;

const BlueprintIconWrapper = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: ${Spacing[2]};
`;

export default BlueprintsAllModalContents;
